import React from 'react';
import { Button, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { User, Pencil, Trash2, Link2Off, Star } from 'lucide-react';
import { formatPhoneNumber } from './FormattedPhoneField';

const ICON_BUTTON_STYLE = {
  width: 28,
  height: 28,
};

const TruncatedLine = ({ text, className = '', style = {}, tooltipId }) => {
  if (!text) return null;

  const content = (
    <div
      className={`text-truncate ${className}`}
      style={{ display: 'inline-block', maxWidth: '100%', verticalAlign: 'top', ...style }}
    >
      {text}
    </div>
  );

  return (
    <OverlayTrigger
      placement="top"
      delay={50}
      overlay={
        <Tooltip className="custom-tooltip" id={tooltipId} style={{ maxWidth: 360 }}>
          {text}
        </Tooltip>
      }
    >
      {content}
    </OverlayTrigger>
  );
};

const ContactCard = ({
  contact,
  index,
  isEditing,
  onEdit,
  onDelete,
  onUnlink,
  isPreferred = false,
  onTogglePreferred,
  showPreferredToggle = false,
}) => {
  const displayName = contact.name?.trim() || 'Untitled contact';
  const title = contact.title?.trim();
  const rawPhone = contact.directPhone?.trim();
  const phone = rawPhone ? formatPhoneNumber(rawPhone) || rawPhone : '';
  const email = contact.directEmail?.trim();
  const cardKey = contact._id ? String(contact._id) : `new-${index}`;

  return (
    <div
      className="contact-card bg-white border d-flex align-items-start h-100"
      style={{
        borderRadius: 12,
        padding: '8px 10px',
        boxShadow: '0 1px 2px rgba(0,0,0,0.06)',
        borderColor: isEditing ? '#111827' : '#dbd9d1',
        minWidth: 0,
        height: '100%',
      }}
    >
      <div className="d-flex align-items-start w-100" style={{ gap: 10, minWidth: 0 }}>
        <div className="position-relative flex-shrink-0 align-self-start">
          <div
            className="d-flex align-items-center justify-content-center"
            style={{ width: 32, height: 32, borderRadius: 8, backgroundColor: '#e6f1fb' }}
          >
            <User size={16} className="text-info" />
          </div>
          {isPreferred && (
            <span
              className="position-absolute d-flex align-items-center justify-content-center bg-white rounded-circle border"
              style={{
                top: -4,
                left: -4,
                width: 14,
                height: 14,
                borderColor: '#f59e0b',
                boxShadow: '0 1px 2px rgba(0,0,0,0.08)',
              }}
              title="Preferred contact"
            >
              <Star size={8} className="text-warning" fill="currentColor" />
            </span>
          )}
        </div>
        <div
          className="d-flex flex-column justify-content-start align-items-start"
          style={{ minWidth: 0, width: 'max-content', maxWidth: 'calc(100% - 42px)' }}
        >
          <TruncatedLine
            text={displayName}
            className="font-weight-bold"
            style={{ fontSize: 13, lineHeight: 1.3 }}
            tooltipId={`contact-name-${cardKey}`}
          />
          {!isEditing && (
            <div
              className="text-muted d-flex flex-column align-items-start"
              style={{ fontSize: 11, lineHeight: 1.3, gap: 2, maxWidth: '100%' }}
            >
              <TruncatedLine text={title} tooltipId={`contact-title-${cardKey}`} />
              <TruncatedLine text={phone} tooltipId={`contact-phone-${cardKey}`} />
              {email ? (
                <TruncatedLine text={email} tooltipId={`contact-email-${cardKey}`} />
              ) : (
                <span>N/A</span>
              )}
            </div>
          )}
        </div>
      </div>
      {(onEdit || onDelete || onUnlink || showPreferredToggle) && (
        <div className="contact-card__actions">
          {showPreferredToggle && onTogglePreferred && (
            <Button
              type="button"
              variant="light"
              size="sm"
              className="p-0 rounded-circle border d-flex align-items-center justify-content-center bg-white"
              style={ICON_BUTTON_STYLE}
              title={isPreferred ? 'Remove preferred contact' : 'Set as preferred contact'}
              onClick={() => onTogglePreferred(contact, !isPreferred)}
            >
              <Star
                size={13}
                className={isPreferred ? 'text-warning' : 'text-muted'}
                fill={isPreferred ? 'currentColor' : 'none'}
              />
            </Button>
          )}
          {onEdit && (
            <Button
              type="button"
              variant="light"
              size="sm"
              className="p-0 rounded-circle border d-flex align-items-center justify-content-center bg-white"
              style={ICON_BUTTON_STYLE}
              title="Edit"
              onClick={() => onEdit(contact)}
            >
              <Pencil size={13} className="text-dark" />
            </Button>
          )}
          {/* Unlink from order — does NOT delete from directory */}
          {onUnlink && (
            <Button
              type="button"
              variant="light"
              size="sm"
              className="p-0 rounded-circle border d-flex align-items-center justify-content-center bg-white"
              style={ICON_BUTTON_STYLE}
              title="Remove from order"
              onClick={() => onUnlink(contact)}
            >
              <Link2Off size={13} className="text-muted" />
            </Button>
          )}
          {/* Delete from directory — backoffice only */}
          {onDelete && (
            <Button
              type="button"
              variant="light"
              size="sm"
              className="p-0 rounded-circle border d-flex align-items-center justify-content-center bg-white"
              style={ICON_BUTTON_STYLE}
              title="Delete from directory"
              onClick={() => onDelete(contact)}
            >
              <Trash2 size={13} className="text-danger" />
            </Button>
          )}
        </div>
      )}
    </div>
  );
};

export default ContactCard;
